import React, { createContext, useContext, useEffect, useRef } from 'react';
import { saveDiaryViewLogGrouped } from 'api/agency/v2/diary.api';

interface SchedulerContextType {
    // 필요한 경우 여기에 메서드 추가
}

const SchedulerContext = createContext<SchedulerContextType | undefined>(undefined);

export const useScheduler = () => {
    const context = useContext(SchedulerContext);
    if (context === undefined) {
        throw new Error('useScheduler must be used within a SchedulerProvider');
    }
    return context;
};

interface SchedulerProviderProps {
    children: React.ReactNode;
}

export const SchedulerProvider: React.FC<SchedulerProviderProps> = ({ children }) => {
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const isProcessingRef = useRef(false);

    // localStorage에서 다이어리 뷰 로그를 가져와서 API 호출
    const processDiaryViewLogs = async () => {
        if (isProcessingRef.current) {
            console.log('🔄 이미 처리 중입니다. 건너뜁니다.');
            return;
        }

        try {
            isProcessingRef.current = true;

            const key = 'agencyDiaryListView';
            const storedData = localStorage.getItem(key);

            if (!storedData) {
                console.log('📱 localStorage에 다이어리 뷰 로그가 없습니다.');
                return;
            }

            let viewData: Record<string, number[]>;
            try {
                viewData = JSON.parse(storedData);
            } catch (e) {
                console.error('❌ localStorage 데이터 파싱 실패:', e);
                localStorage.removeItem(key);
                return;
            }

            if (Object.keys(viewData).length === 0) {
                console.log('📱 처리할 다이어리 뷰 로그가 없습니다.');
                return;
            }

            console.log('📊 처리할 다이어리 뷰 로그:', viewData);

            // if (navigator.sendBeacon) {
            //     navigator.sendBeacon(process.env.REACT_APP_API_URL + "/agency/v2/diary/log/view/grouped", JSON.stringify(viewData));
            // } else {
            //     // API 호출 - localStorage 형태 그대로 전송
            //     await saveDiaryViewLogGrouped(viewData);
            // }
            // API 호출 - localStorage 형태 그대로 전송
            await saveDiaryViewLogGrouped(viewData);
            console.log('✅ 다이어리 뷰 로그 저장 성공');

            // 성공적으로 저장된 경우 localStorage에서 해당 데이터 삭제
            localStorage.removeItem(key);
            console.log('🗑️ localStorage에서 다이어리 뷰 로그 삭제 완료');

        } catch (error) {
            console.error('❌ 다이어리 뷰 로그 처리 중 오류 발생:', error);
        } finally {
            isProcessingRef.current = false;
        }
    };

    // 스케줄러 시작
    const startScheduler = () => {
        if (intervalRef.current) {
            console.log('🔄 스케줄러가 이미 실행 중입니다.');
            return;
        }

        console.log('🚀 다이어리 뷰 로그 스케줄러 시작');

        // 5분마다 실행 (300,000ms)
        intervalRef.current = setInterval(() => {
            // console.log('⏰ 스케줄러 실행:', new Date().toISOString());
            processDiaryViewLogs();
        }, 3000);

        // 초기 실행 (1분 후)
        setTimeout(() => {
            // console.log('🚀 초기 다이어리 뷰 로그 처리 시작');
            processDiaryViewLogs();
        }, 3000);
    };

    // 스케줄러 정지
    const stopScheduler = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            console.log('🛑 다이어리 뷰 로그 스케줄러 정지');
        }
    };

    useEffect(() => {
        // 컴포넌트 마운트 시 스케줄러 시작
        startScheduler();

        // 컴포넌트 언마운트 시 스케줄러 정지 및 정리
        return () => {
            stopScheduler();
        };
    }, []);

    // 페이지 가시성 변경 시 스케줄러 제어
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                console.log('📱 페이지가 숨겨짐 - 스케줄러 정지');
                stopScheduler();
            } else {
                console.log('📱 페이지가 보임 - 스케줄러 시작');
                startScheduler();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    return (
        <SchedulerContext.Provider value={{}}>
            {children}
        </SchedulerContext.Provider>
    );
};

export default SchedulerContext;
